import type {Flow} from "flow-to-typescript-codemod";
// Not a UI component; just an invisible div that contains a hook to detect when
// user viewing discussion.

import * as React from "react";

import {SchedulePolicy, useInterval} from "@khanacademy/wonder-blocks-timing";
import {usePublishAnalyticsEvent} from "@khan/analytics-learning-time";

import {
    mapCurrentPageContentPathFields,
    type ViewHandlerContentPath,
} from "../util/content-path-converter.ts";

type Props = Readonly<{
    contentPath: ViewHandlerContentPath;
    handleInitializePosts: () => void;
    preInitialized: boolean;
    children: React.ReactElement<any>;
}>;

const usePrev = (value: null | boolean | string) => {
    const ref = React.useRef();
    React.useEffect(() => {
        // @ts-expect-error - TS2322 - Type 'string | boolean | null' is not assignable to type 'undefined'.
        ref.current = value;
    });
    return ref.current;
};

export const DiscussionViewHandler: Flow.AbstractComponent<
    Props,
    React.ReactNode
> = React.memo(
    ({
        contentPath,
        handleInitializePosts,
        preInitialized,
        children,
    }: Props): React.ReactElement => {
        const ref = React.useRef<HTMLDivElement | null>(null);
        // Current state of the intersection observer and the previous
        const [isIntersecting, setIntersecting] = React.useState(false);
        const wasIntersecting = usePrev(isIntersecting);
        const {learnableNodeId: contentId} = contentPath;

        const postsInitialized = React.useRef(preInitialized);
        const prevContentId = usePrev(
            postsInitialized.current ? (contentId ?? null) : null,
        );

        const publishEvent = usePublishAnalyticsEvent();
        const viewDuration = React.useRef(0);
        useInterval(() => (viewDuration.current += 0.001), 1, {
            schedulePolicy: isIntersecting
                ? SchedulePolicy.Immediately
                : SchedulePolicy.OnDemand,
        });

        const observer = React.useMemo(
            () =>
                // @ts-expect-error - TS2345 - Argument of type '([entry]: [any]) => void' is not assignable to parameter of type 'IntersectionObserverCallback'.
                new IntersectionObserver(([entry]: [any]) =>
                    setIntersecting(entry.isIntersecting),
                ),
            // We definitely want to re-run this effect if the ref changes, so we
            // disable the exhaustive-deps rule here.
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [ref],
        );

        const handleStartView = React.useCallback(() => {
            // load discussion and start to create event
            if (contentId !== prevContentId) {
                handleInitializePosts();
                postsInitialized.current = true;
            }
            publishEvent({
                eventType: "ContentDiscussionViewStarted",
                eventSchemaVersion: 3,
                ...mapCurrentPageContentPathFields(contentPath),
            });
        }, [
            contentId,
            contentPath,
            handleInitializePosts,
            prevContentId,
            publishEvent,
        ]);

        const handleEndView = React.useCallback(() => {
            // end and submit event
            publishEvent({
                eventType: "ContentDiscussionViewEnded",
                eventSchemaVersion: 3,
                viewDuration: viewDuration.current,
                ...mapCurrentPageContentPathFields(contentPath),
            });
            viewDuration.current = 0;
        }, [contentPath, publishEvent, viewDuration]);

        // initialize and clean up observer
        React.useEffect(() => {
            if (ref.current) {
                observer.observe(ref.current);
            }
            return () => {
                observer.disconnect();
                handleEndView();
            };
        }, [handleEndView, observer, ref]);

        // on intersection change
        React.useEffect(() => {
            if (isIntersecting && !wasIntersecting) {
                handleStartView();
                setIntersecting(true);
            }
            if (!isIntersecting && wasIntersecting) {
                handleEndView();
                setIntersecting(false);
            }
        }, [handleEndView, handleStartView, isIntersecting, wasIntersecting]);

        return <div ref={ref}>{children}</div>;
    },
);
